import { cva } from 'class-variance-authority';
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  memo,
  useMemo,
} from 'react';

import { CenterFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Tooltip from '@/components/Tooltip/TooltipV2';
import { cn } from '@/lib/cn';

import type { ButtonT } from './types';

const Button = (
  props: ComponentPropsWithoutRef<'button'> & ButtonT,
  ref?: ForwardedRef<HTMLButtonElement>,
) => {
  const {
    leftIcon,
    rightIcon,
    onClick,
    width,
    style,
    className,
    rounded,
    sizeType = 's',
    variant = 'primary',
    disabled = false,
    isLoading = false,
    type = 'button',
    tooltip,
    shortcut,
    edgeAlign = 'none',
    ...rest
  } = props;

  const spinnerSize = useMemo(() => {
    switch (sizeType) {
      case 'xl':
        return 18;
      case 'l':
        return 16;
      case 'm':
        return 14;
      case 's':
        return 12;
      default:
        return 10;
    }
  }, [sizeType]);

  const buttonVariants = cva(
    'relative flex items-center justify-center font-normal transition-all duration-150 ease-in-out whitespace-nowrap overflow-hidden cursor-pointer font-medium active:ring-1 active:ring-ring focus:ring-1 focus:ring-ring disabled:active:ring-0 disabled:focus:ring-0 ring-inset',
    {
      variants: {
        variant: {
          primary:
            'bg-primary text-white hover:bg-dune-600 active:bg-dune-600 disabled:bg-dune-400 disabled:text-dune-200 focus:bg-dune-600 shadow-sm disabled:shadow-none',
          outlined:
            'bg-white border border-dune-300 text-foreground hover:bg-dune-50 disabled:text-dune-300 disabled:border-dune-200 disabled:hover:bg-white focus:bg-dune-50 shadow-xs disabled:shadow-none',
          ghost:
            'bg-transparent text-foreground hover:bg-dune-50 active:bg-dune-50 disabled:text-dune-300 disabled:hover:bg-transparent focus:bg-dune-50',
          ghost_secondary:
            'bg-transparent text-muted-foreground hover:bg-dune-100 active:bg-dune-100 disabled:text-dune-300 focus:bg-dune-100 disabled:hover:bg-transparent',
          secondary:
            'bg-secondary text-secondary-foreground hover:bg-dune-200 active:bg-dune-200 disabled:bg-dune-200 disabled:text-dune-400 focus:bg-dune-200 shadow-xs disabled:shadow-none',
          error:
            'bg-destructive text-destructive-foreground hover:bg-red-600 active:bg-red-600 focus:bg-red-600 shadow-xs disabled:bg-dune-400 disabled:text-dune-200 disabled:shadow-none',
          error_outlined:
            'bg-white border border-dune-300 text-destructive hover:bg-dune-50 disabled:border-dune-200 disabled:text-dune-300 focus:bg-dune-50 shadow-xs disabled:shadow-none disabled:hover:bg-white',
          error_ghost:
            'bg-transparent text-destructive hover:bg-dune-50 active:bg-dune-50 disabled:text-dune-300 disabled:hover:bg-transparent',
        },

        size: {
          xl: cn(
            'h-12 text-lg rounded-[8px] min-w-12',
            !props.children && 'w-12',
          ),
          l: cn(
            'h-10 text-base rounded-[8px] min-w-10',
            !props.children && 'w-10',
          ),
          m: cn('h-8 text-sm rounded-[8px] min-w-8', !props.children && 'w-8'),
          s: cn('h-6 text-xs rounded-[6px] min-w-6', !props.children && 'w-6'),
          xs: cn(
            'h-5 text-[10px] rounded-[4px] min-w-5',
            !props.children && 'w-5',
          ),
        },
      },
      defaultVariants: {
        variant: 'primary',
        size: 's',
      },
    },
  );

  const gapBySize = {
    xl: 'gap-1.5',
    l: 'gap-1',
    m: 'gap-1',
    s: 'gap-0.5',
    xs: 'gap-0.5',
  } as const;

  const iconSizeBySize = {
    xl: 'size-[18px]',
    l: 'size-4',
    m: 'size-[14px]',
    s: 'size-3',
    xs: 'size-2.5',
  } as const;

  const paddingBySize = {
    xl: 'p-4.5',
    l: 'p-4',
    m: 'p-3.5',
    s: 'p-2.5',
    xs: 'p-1.75',
  } as const;

  const marginLeftBySize = {
    xl: '-ml-4.5',
    l: '-ml-4',
    m: '-ml-3.5',
    s: '-ml-2.5',
    xs: '-ml-1.75',
  } as const;

  const marginRightBySize = {
    xl: '-mr-4.5',
    l: '-mr-4',
    m: '-mr-3.5',
    s: '-mr-2.5',
    xs: '-mr-1.75',
  } as const;

  return (
    <button
      className={cn(
        buttonVariants({ variant, size: sizeType }),
        disabled && 'cursor-not-allowed',
        isLoading && 'cursor-progress',
        rounded && 'rounded-full',
        edgeAlign === 'left' && marginLeftBySize[sizeType],
        edgeAlign === 'right' && marginRightBySize[sizeType],
        className,
      )}
      disabled={disabled}
      onClick={(e) => {
        if (disabled || isLoading) return;
        e.stopPropagation();
        onClick?.(e);
      }}
      style={{
        width,
        ...style,
      }}
      type={type}
      {...rest}
      ref={ref}
    >
      <Tooltip tooltipNode={tooltip} shortcut={shortcut}>
        <CenterFlex
          f={1}
          className={cn('h-full w-full', paddingBySize[sizeType])}
        >
          <div
            className={cn(
              'absolute top-0 left-0 w-full h-full flex items-center justify-center transition-transform duration-200',
              gapBySize[sizeType],
              !isLoading && 'translate-y-[150%]',
            )}
          >
            {props.spinner || (
              <Spinner size={spinnerSize} color="currentColor" />
            )}
          </div>
          <div
            className={cn(
              'flex items-center justify-center transition-transform duration-200',
              gapBySize[sizeType],
              isLoading && 'translate-y-[-150%]',
            )}
          >
            {leftIcon && (
              <CenterFlex
                className={cn(
                  'shrink-0 h-fit -ml-[0.125em]',
                  iconSizeBySize[sizeType],
                )}
              >
                {leftIcon}
              </CenterFlex>
            )}
            {props.children}
            {rightIcon && (
              <CenterFlex
                className={cn(
                  'shrink-0 h-fit -mr-[0.125em]',
                  iconSizeBySize[sizeType],
                )}
              >
                {rightIcon}
              </CenterFlex>
            )}
          </div>
        </CenterFlex>
      </Tooltip>
    </button>
  );
};

export default memo(forwardRef(Button));
